/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { calculateOffset, useDatatip } from '../PRIVATE_useDatatip';
import {
  DataCursorPosition,
  Group,
  ChartItemData,
  Info,
  Scale,
  ValueFormats
} from '../../utils/UNSAFE_visTypes/chart';
import { BarSeriesData } from '../../UNSAFE_BarChart/barChart.types';
import { Dimension } from '../../utils/UNSAFE_visTypes/common';
import { getDefaultDatatipRenderer } from '../../utils/PRIVATE_chartUtils/dataTipUtils';
import { LineAreaSeriesData } from '../../UNSAFE_LineAreaChart/lineAreaChart.types';
import { getColorRamp } from '../../utils/UNSAFE_visUtils';
import { Placement } from '../../UNSAFE_Floating';
import { useUser } from '../../hooks/UNSAFE_useUser';
import { Property } from 'csstype';

type Props<K extends string | number, D extends ChartItemData<K>> = {
  series: (BarSeriesData<K, D> | LineAreaSeriesData<K, D>)[];
  groups: Group[];
  isDataCursorEnabled?: boolean;
  dataCursorPosition?: DataCursorPosition;
  isHoriz: boolean;
  getDataItemPos: (seriesIndex: number, groupIndex: number) => Dimension | undefined;
  datatip?: (context: { data: D; seriesIndex: number; groupIndex: number; id: K }) => {
    content: ComponentChildren;
    borderColor?: Property.BorderColor;
  };
  xScale: Scale;
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined;
  hoveredItemInfo?: Info;
  focusedItemInfo?: Info;
  width: number;
  height: number;
  valueFormats?: ValueFormats;
  findNearest: (x: number, y: number) => any;
  timeAxisType?: 'mixedFrequency' | 'skipGaps' | 'enabled';
  touchResponse: 'touchStart' | 'touchHold';
  isDisabled: boolean;
};
const DATA_CURSOR_OFFSET = 10;

export const useChartDatatip = <K extends string | number, D extends ChartItemData<K>>({
  isDataCursorEnabled,
  dataCursorPosition,
  isHoriz,
  datatip,
  xScale,
  getDataItem,
  getDataItemPos,
  hoveredItemInfo,
  focusedItemInfo,
  series,
  valueFormats,
  groups,
  width,
  height,
  findNearest,
  timeAxisType,
  touchResponse,
  isDisabled
}: Props<K, D>) => {
  let activeInfo = !isDisabled
    ? focusedItemInfo?.isCurrent
      ? focusedItemInfo
      : hoveredItemInfo?.isCurrent &&
        hoveredItemInfo.seriesIndex !== undefined &&
        hoveredItemInfo.groupIndex !== undefined
      ? hoveredItemInfo
      : undefined
    : undefined;

  const anchor = focusedItemInfo?.isCurrent || isDataCursorEnabled ? 'element' : 'pointer';
  const elementBounds = focusedItemInfo?.isCurrent
    ? getDataItemPos(focusedItemInfo.seriesIndex!, focusedItemInfo.groupIndex!)
    : undefined;

  const { direction } = useUser();
  const isRtl = direction === 'rtl';

  let placement = 'top-start' as Placement;
  let offset = calculateOffset(isRtl, width, elementBounds);
  if (isDataCursorEnabled && dataCursorPosition?.x != null && dataCursorPosition?.y != null) {
    // TOD0: The offset for data cursor should be calculated according to
    // datatip content dimensions as well.
    activeInfo = activeInfo || findNearest?.(dataCursorPosition.x, dataCursorPosition.y);
    const xPos = xScale.transform(dataCursorPosition.x);
    offset = { mainAxis: -1, crossAxis: -1 };
    if (isHoriz) {
      placement = 'end';
      offset.mainAxis = -1 * DATA_CURSOR_OFFSET;
      offset.crossAxis = xPos - height / 2;
    } else {
      placement = 'top';
      offset.crossAxis = isRtl ? width / 2 - xPos : xPos - width / 2;
      offset.mainAxis = -1 * DATA_CURSOR_OFFSET;
    }
  }

  let dtContent;
  let item: D | undefined;
  if (activeInfo?.seriesIndex != null && activeInfo?.groupIndex != null) {
    item = getDataItem(activeInfo.seriesIndex, activeInfo.groupIndex!);
    const datatipContext = item
      ? {
          data: item,
          seriesIndex: activeInfo.seriesIndex,
          groupIndex: activeInfo.groupIndex,
          id: item.id
        }
      : undefined;

    if (datatipContext) {
      const datatipRenderer =
        datatip ||
        getDefaultDatatipRenderer({
          series: series[activeInfo.seriesIndex],
          groups: groups[activeInfo.groupIndex],
          valueFormats,
          timeAxisType
        });
      dtContent = datatipRenderer(datatipContext);
    }
  }
  const colors = getColorRamp();
  const borderColor =
    dtContent?.borderColor ||
    (activeInfo &&
      ((series[activeInfo.seriesIndex!] as BarSeriesData<K, D>).color ||
        (series[activeInfo.seriesIndex!] as LineAreaSeriesData<K, D>).lineColor ||
        (series[activeInfo.seriesIndex!] as LineAreaSeriesData<K, D>).areaColor)) ||
    (activeInfo && colors[activeInfo.seriesIndex! % colors.length]);

  const { datatipContent, datatipProps } = useDatatip({
    content: dtContent?.content,
    anchor,
    placement,
    offset,
    borderColor,
    touchResponse
  });

  return { datatipContent, datatipProps };
};
